import React from "react";
import * as yup from "yup";
import { TFunction } from "i18next/typescript/t";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { App, Flex } from "antd";
import { useTranslation } from "react-i18next";

import {
  HookFormBaseDropdown,
  HookFormInfiniteScrollDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import { SimpleModalProps } from "@/components_v2/Modal/types";
import { CountryResponse, CountryQueryParams } from "@/redux/common/types";
import {
  useCreateOrganizationMutation,
  useLazyGetCountriesQuery,
} from "@/redux/service";
import { handleErrors } from "@/shared/utils/forms";
import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import { OrganizationCommunicationStatuses } from "@/shared/utils/constants";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import { CreateOrganizationFormValues } from "@/features/organizations/list/types";
import { ApiErrorResponse } from "@/redux/types";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

const createOrganizationSchema = (
  t: TFunction,
): yup.ObjectSchema<CreateOrganizationFormValues> =>
  yup.object().shape({
    name: yup
      .string()
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    country: yup.number().typeError(t("form.validation.notValid")),
    address: yup
      .string()
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    email: yup
      .string()
      .email(t("form.validation.notValid"))
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    externalId: yup
      .number()
      .integer(t("form.validation.notValid"))
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    adminEmail: yup
      .string()
      .email(t("form.validation.notValid"))
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    phoneNumber: yup
      .string()
      .typeError(t("form.validation.notValid"))
      .optional(),
    communicatingStatus: yup.string().typeError(t("form.validation.notValid")),
  });

const CreateOrganizationModal = ({ open, setOpen }: SimpleModalProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [getCountries] = useLazyGetCountriesQuery();
  const [createOrganization] = useCreateOrganizationMutation();

  const form = useForm<CreateOrganizationFormValues>({
    resolver: yupResolver(createOrganizationSchema(t)),
    mode: "onChange",
    defaultValues: {
      name: undefined,
      country: undefined,
      address: undefined,
      phoneNumber: undefined,
      email: undefined,
      externalId: undefined,
      adminEmail: undefined,
      communicatingStatus: undefined,
    },
  });

  const closeModal = () => {
    setOpen(false);
    form.reset();
  };

  const handleSubmit = (values: CreateOrganizationFormValues) => {
    createOrganization(values)
      .unwrap()
      .then(() => {
        notification.success({
          message: t("organizations.labels.organizationCreated"),
        });
        closeModal();
      })
      .catch((errors: ApiErrorResponse) => {
        handleErrors(errors, form.setError, notification);
      });
  };

  const fields = [
    {
      label: t("organizations.labels.name"),
      required: true,
      field: (
        <HookFormInput
          name="name"
          required
          placeholder={t("organizations.placeholders.enterName")}
          control={form.control}
        />
      ),
    },
    {
      label: t("organizations.labels.address"),
      required: true,
      field: (
        <HookFormInput
          name="address"
          required
          placeholder={t("organizations.placeholders.enterAddress")}
          control={form.control}
        />
      ),
    },
    {
      label: t("organizations.labels.email"),
      required: true,
      field: (
        <HookFormInput
          name="email"
          required
          placeholder={t("organizations.placeholders.enterEmail")}
          control={form.control}
        />
      ),
    },
    {
      label: t("organizations.labels.externalId"),
      required: true,
      field: (
        <HookFormInput
          name="externalId"
          required
          type="number"
          placeholder={t("organizations.placeholders.enterExternalId")}
          control={form.control}
        />
      ),
    },
    {
      label: t("organizations.labels.adminEmail"),
      required: true,
      field: (
        <HookFormInput
          name="adminEmail"
          required
          placeholder={t("organizations.placeholders.enterAdminEmail")}
          control={form.control}
        />
      ),
    },
    {
      label: t("organizations.labels.communicatingStatus"),
      required: false,
      field: (
        <HookFormBaseDropdown
          name="communicatingStatus"
          control={form.control}
          options={OrganizationCommunicationStatuses.asList()}
          placeholder={t(
            "organizations.placeholders.selectCommunicatingStatus",
          )}
        />
      ),
    },
    {
      label: t("organizations.labels.country"),
      required: false,
      field: (
        <HookFormInfiniteScrollDropdown<CountryResponse, CountryQueryParams>
          name="country"
          control={form.control}
          fetchFn={getCountries}
          labelFieldName="name"
          valueFieldName="id"
          searchFieldName="name"
          placeholder={t("organizations.placeholders.selectCountry")}
        />
      ),
    },
    {
      label: t("organizations.labels.phoneNumber"),
      field: (
        <HookFormInput
          name="phoneNumber"
          placeholder={t("organizations.placeholders.enterPhoneNumber")}
          control={form.control}
        />
      ),
    },
  ];

  return (
    <BaseModal
      title={t("organizations.labels.createOrganization")}
      subtitle={t("organizations.labels.organizationInviteDescription")}
      open={open}
      onCancel={closeModal}
      footer={
        <Flex wrap justify="space-between" align="center" gap={"small"}>
          <BaseButton
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.TERTIARY}
            onClick={() => setOpen(false)}
          >
            {t("buttons.cancel")}
          </BaseButton>

          <BaseButton
            variant="primary"
            size="small"
            disabled={form.formState.isSubmitting}
            onClick={form.handleSubmit(handleSubmit)}
          >
            {t("buttons.submit")}
          </BaseButton>
        </Flex>
      }
    >
      <div className="create-organization-modal-form-fields">
        {fields.map((field) => (
          <FormFieldWrapper
            key={field.label}
            label={field.label}
            required={field.required}
          >
            {field.field}
          </FormFieldWrapper>
        ))}
      </div>
    </BaseModal>
  );
};

export default CreateOrganizationModal;
